import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, useLocation } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/authContext';
import { AcceptInvite } from './invite/AcceptInvite';
import { SetupGuide } from './setup/SetupGuide';
import { ConsoleShell } from './shell/ConsoleShell';
import { PreferencesProvider } from './shell/PreferencesProvider';
import { usePreferences } from './shell/preferencesContext';
import { ErrorBoundary } from './shell/ErrorBoundary';
import { NewBuildNotice } from './shell/NewBuildNotice';
import { SignIn } from './shell/SignIn';

/**
 * The driver simulator, in development builds only.
 *
 * `import.meta.env.DEV` is a compile-time constant, so a production build evaluates
 * this to false and the bundler drops the module entirely rather than shipping code
 * that is merely unreachable. That distinction matters: a page which lets anyone act as
 * a driver — accepting jobs, reporting positions — is a way to steal rides.
 */
const DriverSimulator = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import('./simulator/DriverSimulator')).DriverSimulator }))
  : null;

/**
 * Which whole screen this is.
 *
 * Four cases, in an order that is load-bearing rather than incidental, which is why they
 * are `if`s here rather than routes inside `ConsoleShell`: the console's own routing only
 * begins once there is a session at a firm to route within.
 */
/** The one address the console shows to somebody without a session. */
const SIGN_IN = '/sign-in';

/** What the sign-in screen is handed: where to go back to once it succeeds. */
interface SignInState {
  from?: string;
}

function Whole() {
  const { t } = useTranslation();
  const { pathname, search, state } = useLocation();
  const { signedIn, initialising } = useAuth();

  if (DriverSimulator !== null && pathname === '/simulator') {
    return (
      <Suspense fallback={<p className="text-ink-3 text-center p-10">{t('app.loading')}</p>}>
        <DriverSimulator />
      </Suspense>
    );
  }

  // Rendering sign-in before the stored session has been checked would flash the login
  // form on every reload, which on an eight-hour shift is more than an annoyance.
  if (initialising) return <p className="text-ink-3 text-center p-10">{t('app.loading')}</p>;

  // Ahead of the sign-in screen, because somebody arriving here has no account to sign in
  // with — that is the entire point of the link. Behind the session check, so a person who
  // has already accepted and reloaded lands in the console rather than on a dead link.
  if (!signedIn && pathname === '/invite') return <AcceptInvite />;

  // Sign-in has an address of its own. Drawing the form in place would leave the bar
  // reading `/desk` over a login page, which is a lie about where the person is; the
  // address they were at rides along instead, so that a shift interrupted by an expired
  // session resumes on the screen it was interrupted on.
  if (!signedIn && pathname !== SIGN_IN) {
    return <Navigate to={SIGN_IN} replace state={{ from: `${pathname}${search}` }} />;
  }

  if (!signedIn) return <SignIn />;

  if (pathname === SIGN_IN) {
    return <Navigate to={(state as SignInState | null)?.from ?? '/'} replace />;
  }

  // The person's own console choices are fetched before the shell draws, because the shell
  // reads them once, at mount, and so is which areas are theirs. See PreferencesProvider.
  //
  // The guided setup is a whole screen like the invitation page, not a route inside the
  // shell: the bar, the desk and the telephone are what it walks the person towards, and
  // drawing them behind its first screen would make it look like a dialog over something
  // more important. The shell sends a firm's first administrator here once; the Setup page
  // offers it again. Inside the provider all the same, because it is the administrator's.
  return (
    <PreferencesProvider>
      {pathname === '/setup' ? <SetupDoor /> : <ConsoleShell />}
    </PreferencesProvider>
  );
}

/** The guide for an administrator; the desk for anybody else who types its address. */
function SetupDoor() {
  const { access } = usePreferences();

  return access.settings ? <SetupGuide /> : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <Whole />
        </AuthProvider>
      </ErrorBoundary>
      <NewBuildNotice />
    </BrowserRouter>
  );
}
